import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import { ArrowLeft, MessageCircle, Mail, Phone, MapPin, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  closed: 'bg-gray-100 text-gray-600',
};

const AdminPilotEnquiries = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const adminEmail = getAdminEmail();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { if (adminEmail) fetchEnquiries(); }, [adminEmail]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pilots/enquiries?admin_email=${encodeURIComponent(adminEmail)}`);
      setEnquiries(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load enquiries', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (enquiryId, status) => {
    try {
      await axios.post(
        `${API}/admin/pilots/enquiries/${enquiryId}/status?admin_email=${encodeURIComponent(adminEmail)}`,
        { status }
      );
      toast({ title: 'Updated', description: `Marked ${status}` });
      fetchEnquiries();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const filtered = statusFilter === 'all' ? enquiries : enquiries.filter(e => e.status === statusFilter);
  const stats = {
    total: enquiries.length,
    new: enquiries.filter(e => e.status === 'new').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    closed: enquiries.filter(e => e.status === 'closed').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-[#1e3a5f] text-white py-6">
        <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/admin')} variant="outline" size="sm" className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f]" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6" /> Pilot Enquiries
            </h1>
          </div>
          <Button onClick={fetchEnquiries} variant="outline" className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f]">Refresh</Button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="p-4"><p className="text-sm text-gray-600">Total</p><p className="text-2xl font-bold text-[#1e3a5f]">{stats.total}</p></Card>
          <Card className="p-4"><p className="text-sm text-gray-600">New</p><p className="text-2xl font-bold text-blue-600">{stats.new}</p></Card>
          <Card className="p-4"><p className="text-sm text-gray-600">Contacted</p><p className="text-2xl font-bold text-amber-600">{stats.contacted}</p></Card>
          <Card className="p-4"><p className="text-sm text-gray-600">Closed</p><p className="text-2xl font-bold text-gray-500">{stats.closed}</p></Card>
        </div>

        <div className="flex gap-2 mb-4">
          {['all', 'new', 'contacted', 'closed'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                statusFilter === s ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <Card className="p-8 text-center text-gray-600">Loading...</Card>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center text-gray-600">
            <MessageCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p>No enquiries {statusFilter !== 'all' ? `with status "${statusFilter}"` : 'yet'}.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(e => (
              <Card key={e.id} className="p-4" data-testid={`enquiry-${e.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-[#1e3a5f]">Pilot {e.pilot_code} <span className="text-xs text-gray-500 font-normal">({e.pilot_email})</span></p>
                    <p className="text-xs text-gray-500 mt-0.5">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[e.status] || 'bg-gray-100 text-gray-600'}`}>{(e.status || 'new').toUpperCase()}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  <p><strong className="text-gray-600">Requester:</strong> {e.requester_name}</p>
                  <p className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-400" /> <a href={`mailto:${e.requester_email}`} className="text-blue-600 hover:underline">{e.requester_email}</a></p>
                  {e.requester_phone && <p className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" /> <a href={`tel:${e.requester_phone}`} className="text-blue-600 hover:underline">{e.requester_phone}</a></p>}
                  {e.route && <p className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {e.route}</p>}
                  {e.dates && <p className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-gray-400" /> {e.dates}</p>}
                </div>
                {e.message && (
                  <div className="mt-2 p-2 bg-blue-50 border-l-4 border-blue-400 text-sm rounded">
                    <p className="text-gray-700">&ldquo;{e.message}&rdquo;</p>
                  </div>
                )}

                {/* Status actions */}
                <div className="flex gap-2 mt-3 pt-2 border-t border-dashed">
                  {e.status !== 'contacted' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, 'contacted')} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`mark-contacted-${e.id}`}>
                      Mark Contacted
                    </Button>
                  )}
                  {e.status !== 'closed' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, 'closed')} className="text-gray-700 border-gray-300 hover:bg-gray-50" data-testid={`mark-closed-${e.id}`}>
                      Mark Closed
                    </Button>
                  )}
                  {e.status !== 'new' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, 'new')} className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid={`reopen-${e.id}`}>
                      Reopen
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminPilotEnquiries;
